import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/AdminSidebar';
import { adminGetJobExecution } from '../../lib/adminApi';

const STEP_COLOR  = { 0: '#374151', 1: '#0176CE', 2: '#7c3aed', 3: '#ea580c', 4: '#0f766e', 5: '#d97706' };
const STEP_LABEL  = { 0: 'Jina', 1: 'Haiku', 2: 'Sonnet', 3: 'Haiku+Search', 4: 'Sonnet+Search', 5: 'QC' };
const QC_MODELS   = new Set(['qc_domain_verify', 'qc_naics_requery', 'qc_naics_remap']);
const QC_LABEL    = { qc_domain_verify: 'QC Domain', qc_naics_requery: 'QC NAICS', qc_naics_remap: 'Remap' };
const MODEL_COST  = {
  'claude-haiku-4-5-20251001': { in: 0.80,  out: 4.00  },
  'claude-sonnet-4-6':         { in: 3.00,  out: 15.00 },
};
const STATUS_COLOR = { complete: '#16a34a', processing: '#0176CE', failed: '#dc2626', pending: '#d97706' };

function calcCost(model, inTok, outTok) {
  const c = MODEL_COST[model];
  if (!c) return 0;
  return (inTok / 1e6) * c.in + (outTok / 1e6) * c.out;
}
function fmt$(n)  { return n < 0.001 ? '<$0.001' : `$${n.toFixed(3)}`; }
function fmtMs(ms){ return ms >= 1000 ? `${(ms/1000).toFixed(1)}s` : `${ms}ms`; }

// ── Prompt / Response modal ───────────────────────────────────────────────────

function PromptModal({ call, onClose }) {
  if (!call) return null;
  let parsedResponse = null;
  try { parsedResponse = JSON.parse(call.response_text); } catch {}

  const isQC     = QC_MODELS.has(call.model);
  const badgeColor = isQC ? '#d97706' : (STEP_COLOR[call.step] || '#6b7280');
  const badgeLabel = isQC ? `Step 5 — ${QC_LABEL[call.model] || call.model}` : `Step ${call.step} — ${STEP_LABEL[call.step] || ''}`;

  return (
    <div style={ms.overlay} onClick={onClose}>
      <div style={ms.modal} onClick={e => e.stopPropagation()}>
        <div style={ms.header}>
          <div>
            <span style={{ ...ms.stepBadge, background: badgeColor }}>
              {badgeLabel}
            </span>
            <span style={ms.meta}>{call.name_raw || call.vendor_num}</span>
          </div>
          <button onClick={onClose} style={ms.closeBtn}>✕</button>
        </div>

        <div style={ms.statRow}>
          <div style={ms.stat}><div style={ms.statLabel}>Model</div><div style={ms.statVal}>{call.model}</div></div>
          <div style={ms.stat}><div style={ms.statLabel}>In tokens</div><div style={ms.statVal}>{(call.input_tokens||0).toLocaleString()}</div></div>
          <div style={ms.stat}><div style={ms.statLabel}>Out tokens</div><div style={ms.statVal}>{(call.output_tokens||0).toLocaleString()}</div></div>
          <div style={ms.stat}><div style={ms.statLabel}>Duration</div><div style={ms.statVal}>{fmtMs(call.duration_ms)}</div></div>
          <div style={ms.stat}><div style={ms.statLabel}>Cost</div><div style={{ ...ms.statVal, color: '#16a34a' }}>{fmt$(calcCost(call.model, call.input_tokens||0, call.output_tokens||0))}</div></div>
          {call.domain_found && <div style={ms.stat}><div style={ms.statLabel}>Domain found</div><div style={{ ...ms.statVal, color: '#16a34a' }}>{call.domain_found} ({call.domain_confidence}%)</div></div>}
        </div>

        <div style={ms.body}>
          <div style={ms.pane}>
            <div style={ms.paneLabel}>Prompt</div>
            <pre style={ms.code}>{call.prompt_text || '—'}</pre>
          </div>
          <div style={ms.pane}>
            <div style={ms.paneLabel}>Response</div>
            <pre style={ms.code}>
              {parsedResponse ? JSON.stringify(parsedResponse, null, 2) : (call.response_text || '—')}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Step box in the timeline ──────────────────────────────────────────────────

function StepBox({ call, onClick }) {
  const isJina  = call.model === 'jina_verify';
  const isQC    = QC_MODELS.has(call.model);
  const isRemap = call.model === 'qc_naics_remap';
  const cost    = calcCost(call.model, call.input_tokens||0, call.output_tokens||0);
  const isFinal = call.exit_reason && !isJina && !isQC;
  const color   = isQC ? '#d97706' : (STEP_COLOR[call.step] ?? '#6b7280');
  const label   = isJina ? 'Jina' : isQC ? QC_LABEL[call.model] : `S${call.step} ${STEP_LABEL[call.step] || ''}`;
  const clickable = !isJina && !isRemap;

  // QC outcome from exit_reason
  const qcOutcome = isQC ? (
    call.exit_reason?.includes('confirmed') ? '✓ confirmed' :
    call.exit_reason?.includes('replaced')  ? '↻ replaced' :
    call.exit_reason?.includes('nulled')    ? '✗ nulled' :
    call.exit_reason?.includes('fixed')     ? '✓ fixed' :
    call.exit_reason?.includes('remap')     ? '⇒ remap' :
    call.exit_reason?.includes('null')      ? '— null' : ''
  ) : null;

  return (
    <div
      onClick={clickable ? () => onClick(call) : null}
      style={{
        border: `2px solid ${color}`,
        borderRadius: 7,
        padding: '6px 10px',
        background: isFinal ? color : isQC ? 'rgba(217,119,6,0.06)' : '#fff',
        cursor: clickable ? 'pointer' : 'default',
        minWidth: isRemap ? 70 : 90,
        transition: 'box-shadow 0.15s',
        position: 'relative',
      }}
      title={isJina ? 'Jina domain verification' : isRemap ? call.exit_reason : 'Click to view prompt & response'}
    >
      <div style={{ fontSize: 11, fontWeight: 700, color: isFinal ? '#fff' : color }}>
        {label}
      </div>
      {isQC && qcOutcome && (
        <div style={{ fontSize: 10, color: '#92400e', marginTop: 2 }}>{qcOutcome}</div>
      )}
      {!isJina && !isQC && (
        <>
          <div style={{ fontSize: 10, color: isFinal ? 'rgba(255,255,255,0.8)' : '#6b7280', marginTop: 2 }}>
            {(call.input_tokens||0).toLocaleString()} + {(call.output_tokens||0).toLocaleString()}
          </div>
          <div style={{ fontSize: 10, color: isFinal ? 'rgba(255,255,255,0.9)' : '#16a34a', fontWeight: 600 }}>
            {fmtMs(call.duration_ms)} · {fmt$(cost)}
          </div>
        </>
      )}
      {isQC && !isRemap && (
        <div style={{ fontSize: 10, color: '#92400e', fontWeight: 600, marginTop: 2 }}>
          {fmtMs(call.duration_ms)} · {fmt$(cost)}
        </div>
      )}
      {isFinal && (
        <div style={{ position: 'absolute', top: -8, right: -8, background: '#fff', border: `1.5px solid ${color}`, borderRadius: 10, padding: '1px 5px', fontSize: 9, fontWeight: 700, color: color }}>
          STOP
        </div>
      )}
    </div>
  );
}

// ── One vendor row ────────────────────────────────────────────────────────────

function VendorRow({ row, onClickCall }) {
  const nonJina = row.calls.filter(c => c.model !== 'jina_verify');
  const domain  = row.calls.find(c => c.domain_found)?.domain_found;
  const totalCost = nonJina.reduce((s, c) => s + calcCost(c.model, c.input_tokens||0, c.output_tokens||0), 0);
  const totalMs   = row.calls.reduce((s, c) => s + (c.duration_ms||0), 0);
  const stepsHit  = nonJina.length;

  return (
    <div style={vs.row}>
      {/* Left: vendor info */}
      <div style={vs.nameCol}>
        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>#{row.row_number}</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#0a2540', marginTop: 1, wordBreak: 'break-word' }}>
          {row.name_raw || row.vendor_num || '—'}
        </div>
        {domain && <div style={{ fontSize: 11, color: '#16a34a', fontWeight: 600, marginTop: 2 }}>{domain}</div>}
        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>
          {stepsHit} step{stepsHit!==1?'s':''} · {fmtMs(totalMs)} · <span style={{ color: '#16a34a' }}>{fmt$(totalCost)}</span>
        </div>
      </div>

      {/* Right: step timeline */}
      <div style={vs.timeline}>
        {row.calls.map((call, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {i > 0 && <div style={vs.arrow}>→</div>}
            <StepBox call={call} onClick={onClickCall} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Summary bar ───────────────────────────────────────────────────────────────

function SummaryBar({ summary, totalRows }) {
  if (!summary?.length) return null;
  // Steps 1&3 = haiku, 2&4 = sonnet, 5 = haiku (QC)
  const stepModel = s => (s === 2 || s === 4) ? 'claude-sonnet-4-6' : 'claude-haiku-4-5-20251001';
  const totCost = summary.reduce((s, r) => s + calcCost(stepModel(r.step), r.total_input||0, r.total_output||0), 0);

  // Separate QC step from main steps
  const mainSteps = summary.filter(r => r.step < 5);
  const qcStep    = summary.find(r => r.step === 5);

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {mainSteps.map(r => {
          const cost = calcCost(stepModel(r.step), r.total_input||0, r.total_output||0);
          return (
            <div key={r.step} style={{ ...ss.card, borderTop: `3px solid ${STEP_COLOR[r.step]}` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: STEP_COLOR[r.step], marginBottom: 6 }}>Step {r.step} — {STEP_LABEL[r.step]}</div>
              <div style={ss.row}><span>Rows</span><strong>{r.rows} / {totalRows}</strong></div>
              <div style={ss.row}><span>Avg time</span><strong>{fmtMs(r.avg_ms)}</strong></div>
              <div style={ss.row}><span>In tokens</span><strong>{(r.total_input||0).toLocaleString()}</strong></div>
              <div style={ss.row}><span>Out tokens</span><strong>{(r.total_output||0).toLocaleString()}</strong></div>
              <div style={{ ...ss.row, color: '#16a34a', fontWeight: 700 }}><span>Cost</span><strong>{fmt$(cost)}</strong></div>
            </div>
          );
        })}
        <div style={{ ...ss.card, borderTop: '3px solid #374151', background: '#0a2540', flex: '0 0 auto' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 6 }}>TOTAL</div>
          <div style={ss.row}><span style={{ color: '#94a3b8' }}>Records</span><strong style={{ color: '#fff' }}>{totalRows}</strong></div>
          <div style={ss.row}><span style={{ color: '#94a3b8' }}>Per record</span><strong style={{ color: '#fff' }}>{fmt$(totCost / Math.max(1, totalRows))}</strong></div>
          <div style={ss.row}><span style={{ color: '#94a3b8' }}>Total cost</span><strong style={{ color: '#4ade80', fontSize: 15 }}>{fmt$(totCost)}</strong></div>
        </div>
      </div>

      {qcStep && (
        <div style={{ marginTop: 10, background: 'rgba(217,119,6,0.06)', border: '1px solid rgba(217,119,6,0.25)', borderRadius: 8, padding: '10px 16px', display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#d97706' }}>Step 5 — Quality Control</div>
          <div style={{ fontSize: 12, color: '#92400e' }}>Rows triggered: <strong>{qcStep.rows}</strong></div>
          <div style={{ fontSize: 12, color: '#92400e' }}>Avg time: <strong>{fmtMs(qcStep.avg_ms)}</strong></div>
          <div style={{ fontSize: 12, color: '#92400e' }}>Tokens: <strong>{(qcStep.total_input||0).toLocaleString()} in / {(qcStep.total_output||0).toLocaleString()} out</strong></div>
          <div style={{ fontSize: 12, color: '#16a34a', fontWeight: 700 }}>Cost: <strong>{fmt$(calcCost(stepModel(5), qcStep.total_input||0, qcStep.total_output||0))}</strong></div>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminJobExecution() {
  const { jobId }  = useParams();
  const navigate   = useNavigate();
  const [data,     setData]     = useState(null);
  const [error,    setError]    = useState(null);
  const [modal,    setModal]    = useState(null); // call object for modal
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const d = await adminGetJobExecution(jobId);
      setData(d);
      if (d.job?.status === 'processing') {
        pollRef.current = setTimeout(load, 3000);
      }
    } catch (e) {
      setError('Failed to load: ' + (e?.message || e));
    }
  };

  useEffect(() => {
    load();
    return () => clearTimeout(pollRef.current);
  }, [jobId]);

  if (error) return <div style={s.page}><AdminSidebar active="jobs" /><main style={s.main}><div style={{ color: '#dc2626' }}>{error}</div></main></div>;
  if (!data)  return <div style={s.page}><AdminSidebar active="jobs" /><main style={s.main}><div style={{ color: '#6b7280' }}>Loading…</div></main></div>;

  const { job, step_summary, calls } = data;
  const isLive = job.status === 'processing';
  const pct    = job.record_count ? Math.round((job.processed_count||0) / job.record_count * 100) : 0;

  // Group calls by row_number
  const rowMap = new Map();
  for (const c of (calls || [])) {
    if (!rowMap.has(c.row_number)) rowMap.set(c.row_number, { row_number: c.row_number, name_raw: c.name_raw, vendor_num: c.vendor_num, calls: [] });
    rowMap.get(c.row_number).calls.push(c);
  }
  // Sort each row's calls: by step, then non-Jina before Jina within the same step, remaps last in step 5
  for (const row of rowMap.values()) {
    row.calls.sort((a, b) => {
      if (a.step !== b.step) return a.step - b.step;
      const aJ = a.model === 'jina_verify' ? 1 : a.model === 'qc_naics_remap' ? 2 : 0;
      const bJ = b.model === 'jina_verify' ? 1 : b.model === 'qc_naics_remap' ? 2 : 0;
      return aJ - bJ;
    });
  }
  const rows = [...rowMap.values()].sort((a, b) => a.row_number - b.row_number);

  // Attach name_raw to modal call when it comes from the flat list
  const openModal = (call) => {
    const row = rowMap.get(call.row_number);
    setModal({ ...call, name_raw: row?.name_raw || call.vendor_num });
  };

  return (
    <div style={s.page}>
      <AdminSidebar active="jobs" />
      <main style={s.main}>
        <PromptModal call={modal} onClose={() => setModal(null)} />

        {/* Header */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <button onClick={() => navigate('/admin/jobs')} style={s.backBtn}>← Job Queue</button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <h1 style={s.title}>Job #{job.job_id} — Execution Log</h1>
                {isLive && <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 700 }}>● LIVE</span>}
                <span style={{ fontSize: 13, fontWeight: 600, color: STATUS_COLOR[job.status] || '#6b7280' }}>{job.status}</span>
              </div>
              <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>{job.tenant_name} · {job.file_name}</div>
            </div>
            <button onClick={() => navigate(`/admin/jobs/${jobId}/metrics`)} style={s.metricsBtn}>Metrics →</button>
          </div>

          {/* Progress bar */}
          <div style={{ marginTop: 14, background: '#e5e7eb', borderRadius: 6, height: 8, overflow: 'hidden', maxWidth: 600 }}>
            <div style={{ width: `${pct}%`, background: isLive ? '#0176CE' : '#16a34a', height: '100%', borderRadius: 6, transition: 'width 0.5s ease' }} />
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
            {job.processed_count ?? 0} of {job.record_count} records processed ({pct}%)
          </div>
        </div>

        <SummaryBar summary={step_summary} totalRows={job.record_count || 0} />

        {rows.length === 0 && (
          <div style={{ color: '#6b7280', fontSize: 14, padding: '40px 0', textAlign: 'center' }}>
            {isLive ? 'Waiting for first records to start processing…' : 'No execution data — job used the old pipeline.'}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rows.map(row => <VendorRow key={row.row_number} row={row} onClickCall={openModal} />)}
        </div>

      </main>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s = {
  page:      { display: 'flex', minHeight: '100vh', background: '#f8fafc' },
  main:      { flex: 1, padding: '32px 40px', overflowY: 'auto' },
  backBtn:   { background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 13, padding: '0 0 6px 0', display: 'block' },
  title:     { fontSize: 22, fontWeight: 700, color: '#0a2540', margin: 0 },
  metricsBtn:{ background: '#f3f4f6', border: '1px solid #e2e8f0', color: '#374151', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};

const ss = {
  card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px', flex: '1 1 140px', minWidth: 130 },
  row:  { display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6b7280', padding: '2px 0' },
};

const vs = {
  row:      { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px', display: 'flex', alignItems: 'flex-start', gap: 16 },
  nameCol:  { width: 200, flexShrink: 0 },
  timeline: { display: 'flex', alignItems: 'center', gap: 0, flexWrap: 'wrap', flex: 1, rowGap: 8 },
  arrow:    { color: '#d1d5db', fontSize: 14, padding: '0 2px' },
};

const ms = {
  overlay:   { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px', overflowY: 'auto' },
  modal:     { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 1100, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden' },
  header:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' },
  stepBadge: { color: '#fff', borderRadius: 5, padding: '3px 10px', fontSize: 12, fontWeight: 700, marginRight: 10 },
  meta:      { fontSize: 14, fontWeight: 600, color: '#0a2540' },
  closeBtn:  { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6b7280', padding: '0 4px' },
  statRow:   { display: 'flex', gap: 24, padding: '12px 24px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' },
  stat:      { display: 'flex', flexDirection: 'column', gap: 2 },
  statLabel: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9ca3af' },
  statVal:   { fontSize: 14, fontWeight: 600, color: '#0a2540' },
  body:      { display: 'grid', gridTemplateColumns: '1fr 1fr', height: '65vh' },
  pane:      { padding: '16px 20px', overflowY: 'auto', borderRight: '1px solid #f1f5f9' },
  paneLabel: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#94a3b8', marginBottom: 10 },
  code:      { fontSize: 12, lineHeight: 1.65, fontFamily: "'SF Mono', 'Monaco', monospace", color: '#374151', whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 },
};
